/**
 * Store Locator avec composition GMap - Version 2.0.0
 * Compatible avec Shopify Liquid et intégration thème
 */

class StoreLocator {
  constructor(options = {}) {
    // Protection contre la double initialisation
    if (this.constructor._instance) {
      console.warn(
        "StoreLocator déjà initialisé, retour de l'instance existante",
      );
      return this.constructor._instance;
    }
    this.constructor._instance = this;

    this.config = {
      containerId: "store-locator",
      googleMapsApiKey: options.googleMapsApiKey || "",
      dataSource: options.dataSource || "shopify",
      storyblokToken: options.storyblokToken || "rMn0oYOzWSyyTSkfymrr2wtt",
      locale: options.locale || "fr",
      iconMarker: options.iconMarker || "/assets/marker.svg", // Pour tous les points de vente
      iconMarkerReseller:
        options.iconMarkerReseller || "/assets/marker-reseller.svg", // Pour les clusters
      clusterImage: options.clusterImage || "/assets/cluster.png", // Fallback pour l'ancienne API
      defaultCenter: { lat: 46.2276, lng: 2.2137 },
      defaultZoom: 6,
      maxZoom: 15,
      gridSize: 50,
      themeIntegration: options.themeIntegration || false,
      useThemeComponents: options.useThemeComponents || false,
      ...options,
    };

    // Détecter si on est sur desktop (>1000px)
    this.isDesktop = window.innerWidth > 1000;

    this.state = {
      stores: [],
      filteredStores: [],
      currentStore: null,
      currentCoordinates: null,
      translations: null,
      map: null,
      markers: [],
      markerClusterer: null,
      geocoder: null,
      google: null,
      isLoading: true,
      showList: true, // Toujours afficher la liste
      showMap: this.isDesktop ? true : false, // Afficher la carte sur desktop
      searchAddress: "",
      selectedType: "all",
      selectedRadius: 0,
      types: [],
    };

    // Instance de GMap pour la composition
    this.gmapInstance = null;
    this.gmapContainer = null;

    // Cache et gestionnaires d'événements
    this.cache = new Map();
    this.eventListeners = new Map();

    // Initialisation asynchrone
    setTimeout(() => this.init(), 0);
  }

  /**
   * Initialisation du composant avec composition GMap
   */
  async init() {
    try {
      this.container = document.getElementById(this.config.containerId);
      if (!this.container) {
        throw new Error(`Container "${this.config.containerId}" non trouvé`);
      }

      this.renderLoading();

      // Chargement des données d'abord
      await this.loadData();

      // Rendu de l'interface pour que le conteneur soit visible
      this.render();
      this.bindEvents();

      // Gérer le redimensionnement de la fenêtre
      this.handleResize();

      this.state.isLoading = false;
      this.updateUI();

      // Initialiser les valeurs par défaut des selects après le rendu
      setTimeout(() => {
        const typeSelect = document.getElementById("store-type");
        const radiusSelect = document.getElementById("search-radius");

        // Définir les valeurs par défaut pour déclencher l'animation des labels flottants
        if (typeSelect) {
          typeSelect.value =
            this.state.selectedType === "all"
              ? "all"
              : this.state.selectedType || "all";
          typeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
        if (radiusSelect) {
          radiusSelect.value = this.state.selectedRadius?.toString() || "0";
          radiusSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }, 100);

      // Initialisation de la carte Google Maps APRÈS le rendu
      // Sur desktop, initialiser la carte immédiatement
      if (this.isDesktop || this.state.showMap) {
        await this.initGMap();
      }

      // Créer les marqueurs après l'initialisation complète
      if (this.state.map && this.state.filteredStores.length > 0) {
        this.createMarkers();
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation:", error);
      this.handleError(error, "init");
    }
  }

  /**
   * Initialisation de la carte Google Maps (mode fallback uniquement)
   */
  async initGMap() {
    try {
      // Charger l'API Google Maps
      await this.loadGoogleMapsAPI();

      // Créer ou trouver le conteneur de carte
      let mapContainer = document.getElementById("google-map");
      if (!mapContainer) {
        // Créer le conteneur de carte s'il n'existe pas
        mapContainer = document.createElement("div");
        mapContainer.id = "google-map";
        mapContainer.className = "store-locator-map";

        // Définir les dimensions CSS pour que Google Maps s'affiche
        mapContainer.style.width = "100%";
        mapContainer.style.height = "400px";
        mapContainer.style.minHeight = "400px";
        mapContainer.style.borderRadius = "8px";
        mapContainer.style.overflow = "hidden";

        // Ajouter le conteneur au conteneur principal
        if (this.container) {
          this.container.appendChild(mapContainer);
        }
      } else {
        // S'assurer que le conteneur existant a les bonnes dimensions
        mapContainer.style.width = "100%";
        mapContainer.style.height = "400px";
        mapContainer.style.minHeight = "400px";
      }

      // Initialiser la carte Google Maps
      this.state.map = new google.maps.Map(mapContainer, {
        center: this.config.defaultCenter,
        zoom: this.config.defaultZoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: this.getMapStyles(),
        gestureHandling: "cooperative",
      });

      // Initialiser le géocodeur
      this.state.geocoder = new google.maps.Geocoder();
      this.state.google = window.google;

      // Forcer le redimensionnement de la carte après un délai
      setTimeout(() => {
        if (this.state.map) {
          google.maps.event.trigger(this.state.map, "resize");

          // Vérifier que la carte est bien visible
          const mapDiv = this.state.map.getDiv();
          if (mapDiv && mapDiv.offsetWidth === 0 && mapDiv.offsetHeight === 0) {
            setTimeout(() => {
              google.maps.event.trigger(this.state.map, "resize");
            }, 500);
          }
        }
      }, 300);
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la carte:", error);
      throw error;
    }
  }

  /**
   * Obtenir les styles de carte personnalisés
   */
  getMapStyles() {
    return [
      {
        featureType: "all",
        elementType: "geometry",
        stylers: [{ color: "#f5f5f5" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#c9c9c9" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#757575" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#e5e5e5" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#ffffff" }],
      },
      {
        featureType: "road.arterial",
        elementType: "labels.text.fill",
        stylers: [{ color: "#757575" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#dadada" }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#616161" }],
      },
      {
        featureType: "road.local",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9e9e9e" }],
      },
      {
        featureType: "transit.line",
        elementType: "geometry",
        stylers: [{ color: "#e5e5e5" }],
      },
      {
        featureType: "transit.station",
        elementType: "geometry",
        stylers: [{ color: "#eeeeee" }],
      },
      {
        featureType: "administrative",
        elementType: "labels.text.fill",
        stylers: [{ color: "#bdbdbd" }],
      },
    ];
  }

  /**
   * Obtenir l'adresse du centre par défaut
   */
  getCenterAddress() {
    const { lat, lng } = this.config.defaultCenter;
    return `${lat}, ${lng}`;
  }

  /**
   * Chargement de l'API Google Maps avec la bibliothèque geometry
   */
  async loadGoogleMapsAPI() {
    return new Promise((resolve, reject) => {
      // Si l'API est déjà chargée avec geometry
      if (window.google?.maps?.geometry) {
        resolve();
        return;
      }

      // Vérifier si le script est déjà en cours de chargement
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        // Attendre que le script se charge
        const checkGoogle = () => {
          if (window.google?.maps?.geometry) {
            resolve();
          } else if (window.google?.maps) {
            // Google Maps est chargé mais pas geometry, on doit le charger
            this.loadGeometryLibrary();
            resolve();
          } else {
            setTimeout(checkGoogle, 100);
          }
        };
        checkGoogle();
        return;
      }

      // Charger l'API Google Maps avec geometry
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.config.googleMapsApiKey}&libraries=geometry&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      // Callback global pour l'initialisation
      window.initGoogleMaps = () => {
        resolve();
        delete window.initGoogleMaps;
      };

      script.onerror = () => {
        reject(new Error("Erreur lors du chargement de l'API Google Maps"));
        delete window.initGoogleMaps;
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Chargement de la bibliothèque geometry si elle n'est pas disponible
   */
  loadGeometryLibrary() {
    if (!window.google?.maps?.geometry) {
      console.warn(
        "Bibliothèque geometry non disponible, utilisation d'un calcul de distance simplifié",
      );
    }
  }

  /**
   * Chargement des données (magasins et traductions) selon la source
   */
  async loadData() {
    if (this.config.dataSource === "shopify") {
      await this.loadShopifyData();
    } else {
      await this.loadStoryblokData();
    }
  }

  /**
   * Chargement des données depuis Shopify (Metaobjects)
   */
  async loadShopifyData() {
    try {
      // Chargement des magasins depuis les données globales Shopify
      // Vérifier si GraphQL a déjà chargé les données (race condition fix)
      const hasGraphQLData =
        window.shopifyStoresConfig?.useGraphQL === true &&
        window.shopifyStoresData &&
        Array.isArray(window.shopifyStoresData) &&
        window.shopifyStoresData.length > 250;

      if (window.shopifyStoresData && Array.isArray(window.shopifyStoresData)) {
        this.state.stores = window.shopifyStoresData;
        this.state.filteredStores = [...this.state.stores];
        this.extractTypes();
      } else {
        console.warn(
          "Aucune donnée de magasins Shopify trouvée dans window.shopifyStoresData",
        );
        this.state.stores = [];
        this.state.filteredStores = [];
      }

      // Chargement des traductions depuis les données globales Shopify
      if (window.shopifyStoresTranslations) {
        this.state.translations = window.shopifyStoresTranslations;
      } else {
        this.state.translations = this.getDefaultTranslations();
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données Shopify:", error);
      this.state.stores = [];
      this.state.filteredStores = [];
      this.state.translations = this.getDefaultTranslations();
    }
  }

  /**
   * Chargement des données depuis Storyblok (mode legacy)
   */
  async loadStoryblokData() {
    await this.loadTranslations();
    await this.loadStores();
  }

  /**
   * Chargement des traductions depuis Storyblok (mode legacy)
   */
  async loadTranslations() {
    const cacheKey = `translations_${this.config.locale}`;
    const cached = this.getCachedData(cacheKey);

    if (cached) {
      this.state.translations = cached;
      return;
    }

    try {
      const response = await fetch(
        `https://api.storyblok.com/v2/cdn/stories/translations_storeloc?token=${this.config.storyblokToken}&version=published&resolve_links=url&language=${this.config.locale}`,
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      this.state.translations = data.story;
      this.setCachedData(cacheKey, this.state.translations, 300000);
    } catch (error) {
      console.warn("Erreur lors du chargement des traductions:", error);
      this.state.translations = this.getDefaultTranslations();
    }
  }

  /**
   * Traductions par défaut
   */
  getDefaultTranslations() {
    return {
      content: {
        search_placeholder: "Rechercher une adresse...",
        no_results: "Aucun magasin trouvé",
        distance: "Distance",
        opening_hours: "Horaires d'ouverture",
        contact: "Contact",
        directions: "Itinéraire",
        cta_display_list: "Vue liste",
        cta_display_map: "Carte",
        search_button: "Rechercher",
        reset_button: "Réinitialiser",
        type_all: "Tous les types",
        radius_all: "Tous les rayons",
      },
    };
  }

  /**
   * Chargement des magasins depuis Storyblok
   */
  async loadStores() {
    const cacheKey = `stores_${this.config.locale}`;
    const cached = this.getCachedData(cacheKey);

    if (cached) {
      this.state.stores = cached;
      this.state.filteredStores = [...cached];
      this.extractTypes();
      return;
    }

    try {
      const response = await fetch(
        `https://api.storyblok.com/v2/cdn/stories/?token=${this.config.storyblokToken}&version=published&resolve_links=url&starts_with=stores/&language=${this.config.locale}&per_page=100`,
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      this.state.stores = data.stories || [];
      this.state.filteredStores = [...this.state.stores];
      this.extractTypes();
      this.setCachedData(cacheKey, this.state.stores, 300000);
    } catch (error) {
      console.error("Erreur lors du chargement des magasins:", error);
      this.state.stores = [];
      this.state.filteredStores = [];
    }
  }

  /**
   * Extraction des types de magasins
   */
  extractTypes() {
    const types = new Set();
    this.state.stores.forEach((store) => {
      if (store.content?.type_of_store) {
        types.add(store.content.type_of_store);
      }
    });
    this.state.types = Array.from(types);
  }

  /**
   * Rendu de l'interface utilisateur
   */
  render() {
    this.container.innerHTML = `
      <div class="store-locator">
        
        <div class="store-locator__content">
          <div class="store-locator__search">
            ${this.renderSearchForm()}
          </div>
          
          <div class="store-locator__controls">
            <div class="store-locator__view-toggle">
              <button class="store-locator__toggle-btn ${
                this.state.showList ? "active" : ""
              }" data-view="list">
                ${
                  this.state.translations?.content?.cta_display_list ||
                  "Vue liste"
                }
              </button>
              <button class="store-locator__toggle-btn ${
                this.state.showMap ? "active" : ""
              }" data-view="map">
                ${this.state.translations?.content?.cta_display_map || "Carte"}
              </button>
            </div>
          </div>
          
          <div class="store-locator__main">
            <div class="store-locator__list ${
              this.state.showList ? "active" : ""
            }" id="store-list">
              ${this.renderStoreList()}
            </div>
            
            <div class="store-locator__map ${
              this.state.showMap ? "active" : ""
            }" id="store-map">
              ${this.renderMapContainer()}
            </div>
          </div>
        </div>
        
        ${this.renderStoreModal()}
      </div>
    `;
  }

  /**
   * Rendu du formulaire de recherche avec classes du thème
   */
  renderSearchForm() {
    return `
      <form class="store-locator-search" id="search-form">
        <div class="field relative grid">
          <input 
            type="text" 
            id="search-address" 
            class="input is-floating" 
            placeholder=" "
            value="${this.state.searchAddress}"
          >
          <label class="label is-floating" for="search-address">
            ${
              this.state.translations?.content?.search_placeholder ||
              "Rechercher une adresse..."
            }
          </label>
          <div class="self-submit-button absolute">
            <button type="button" id="geolocation-btn" title="Utiliser ma position" class="flex justify-center items-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="grid gap-4d5 md:gap-6" style="grid-template-columns: repeat(2, 1fr);">
          <div class="field">
            <select id="store-type" class="select is-floating" required>
              <option value="" disabled selected></option>
              <option value="all">${
                this.state.translations?.content?.type_all || "Tous les types"
              }</option>
              ${this.state.types
                .map(
                  (type) => `
                <option value="${type}" ${
                  this.state.selectedType === type ? "selected" : ""
                }>${type}</option>
              `,
                )
                .join("")}
            </select>
            <svg class="icon icon-chevron-up icon-sm absolute pointer-events-none" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
            </svg>
            <label class="label is-floating" for="store-type">Type de magasin</label>
          </div>
          
          <div class="field">
            <select id="search-radius" class="select is-floating" required>
              <option value="" disabled selected></option>
              <option value="0">${
                this.state.translations?.content?.radius_all ||
                "Tous les rayons"
              }</option>
              <option value="10" ${
                this.state.selectedRadius === 10 ? "selected" : ""
              }>10 km</option>
              <option value="25" ${
                this.state.selectedRadius === 25 ? "selected" : ""
              }>25 km</option>
              <option value="100" ${
                this.state.selectedRadius === 100 ? "selected" : ""
              }>100 km</option>
              <option value="250" ${
                this.state.selectedRadius === 250 ? "selected" : ""
              }>250 km</option>
            </select>
            <svg class="icon icon-chevron-up icon-sm absolute pointer-events-none" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
            </svg>
            <label class="label is-floating" for="search-radius">Rayon de recherche</label>
          </div>
        </div>
        
        <div class="flex gap-4d5 md:gap-6 justify-center">
          <button type="submit" class="button button--primary" is="hover-button">
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">${
                this.state.translations?.content?.search_button || "Rechercher"
              }</span>
          </button>
          <button type="button" class="button button--secondary" id="reset-search" is="hover-button">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">${
              this.state.translations?.content?.reset_button || "Réinitialiser"
            }</span>
          </button>
        </div>
      </form>
    `;
  }

  /**
   * Rendu de la liste des magasins
   */
  renderStoreList() {
    if (this.state.filteredStores.length === 0) {
      return `
        <div class="store-locator-list__empty">
          <p>${
            this.state.translations?.content?.no_results ||
            "Aucun magasin trouvé"
          }</p>
        </div>
      `;
    }

    return `
      <div class="store-locator-list">
        ${this.state.filteredStores
          .map((store) => this.renderStoreItem(store))
          .join("")}
      </div>
    `;
  }

  /**
   * Rendu d'un élément de magasin
   */
  renderStoreItem(store) {
    const distance = store.distance ? `${store.distance.toFixed(1)} km` : "";
    const isActive =
      this.state.currentStore && this.state.currentStore.slug === store.slug;

    return `
      <div class="store-locator-item ${
        isActive ? "active" : ""
      }" data-store-slug="${store.slug}">
        <div class="store-locator-item__header">
          <h3 class="store-locator-item__name">${
            store.content?.name || store.name
          }</h3>
          ${
            distance
              ? `<span class="store-locator-item__distance">${distance}</span>`
              : ""
          }
        </div>
        
        <div class="store-locator-item__content">
          <div class="store-locator-item__address">
            <p>${store.content?.location?.address || ""}</p>
            <p>${store.content?.location?.postal_code || ""} ${
              store.content?.location?.town || ""
            }</p>
          </div>
          
          ${
            (store.content?.contact?.phone &&
              store.content.contact.phone.trim() !== "" &&
              store.content.contact.phone !== null) ||
            (store.content?.contact?.email &&
              store.content.contact.email.trim() !== "" &&
              store.content.contact.email !== null)
              ? `
            <div class="store-locator-item__contact">
              ${
                store.content?.contact?.phone &&
                store.content.contact.phone.trim() !== "" &&
                store.content.contact.phone !== null
                  ? `
                <a href="tel:${store.content.contact.phone}" class="store-locator- flex flex-row gap-2">
                  <svg class="icon icon-phone icon-sm" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H8.15287C8.64171 3 9.05816 3.35341 9.13927 3.8356L9.87858 7.27147C9.95475 7.72651 9.73606 8.17726 9.33967 8.40699L7.79131 9.40552C8.9141 11.8113 10.6887 13.5859 13.0945 14.7087L14.093 13.1603C14.3227 12.7639 14.7735 12.5452 15.2285 12.6214L18.6644 13.3607C19.1466 13.4418 19.5 13.8583 19.5 14.3471V17.5C19.5 18.0304 19.2893 18.5391 18.9142 18.9142C18.5391 19.2893 18.0304 19.5 17.5 19.5H16.5C8.54416 19.5 2.25 13.2058 2.25 5.25V4.5C2.25 3.96957 2.46071 3.46086 2.83579 3.08579C3.21086 2.71071 3.71957 2.5 4.25 2.5H7.5C8.03043 2.5 8.53914 2.71071 8.91421 3.08579C9.28929 3.46086 9.5 3.96957 9.5 4.5V5.25"></path>
                  </svg>
                  ${store.content.contact.phone}
                </a>
              `
                  : ""
              }
              ${
                store.content?.contact?.email &&
                store.content.contact.email.trim() !== "" &&
                store.content.contact.email !== null
                  ? `
                <a href="mailto:${store.content.contact.email}" class="store-locator-item__email flex flex-row gap-2">
                  <svg class="icon icon-email icon-sm" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"></path>
                  </svg>
                  ${store.content.contact.email}
                </a>
              `
                  : ""
              }
            </div>
          `
              : ""
          }
          
          ${
            this.hasOpeningHours(store.content?.opening_hours)
              ? `
            <div class="store-locator-item__hours">
              <h4>${
                this.state.translations?.content?.opening_hours || "Horaires"
              }</h4>
              <div class="store-locator-item__hours-list">
                ${this.renderOpeningHours(store.content.opening_hours)}
              </div>
            </div>
          `
              : ""
          }
          
          <div class="store-locator-item__actions">
            <button class="store-locator-item__select button button--primary button--small" is="hover-button" data-store-slug="${
              store.slug
            }">
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">Sélectionner</span>
            </button>
            ${
              store.content?.location?.latitude &&
              store.content?.location?.longitude
                ? `
              <a 
                href="https://www.google.com/maps/dir/?api=1&destination=${
                  store.content.location.latitude
                },${store.content.location.longitude}" 
                target="_blank" 
                class="store-locator-item__directions button button--secondary button--small" is="hover-link"
              >
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">${
                this.state.translations?.content?.directions || "Itinéraire"
              }</span>
              </a>
            `
                : ""
            }
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Vérifie si les horaires d'ouverture sont renseignés
   */
  hasOpeningHours(hours) {
    if (!hours || typeof hours !== "object") {
      return false;
    }

    // Vérifier si au moins un jour a des horaires renseignés
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    return days.some((day) => {
      const dayHours = hours[day];
      return (
        dayHours && dayHours.trim() !== "" && dayHours.toLowerCase() !== "fermé"
      );
    });
  }

  /**
   * Rendu des horaires d'ouverture
   */
  renderOpeningHours(hours) {
    if (!this.hasOpeningHours(hours)) {
      return "";
    }

    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const dayNames = [
      "Lundi",
      "Mardi",
      "Mercredi",
      "Jeudi",
      "Vendredi",
      "Samedi",
      "Dimanche",
    ];

    return days
      .map((day, index) => {
        const dayHours = hours[day];
        // Ne pas afficher les jours sans horaires renseignés
        if (!dayHours || dayHours.trim() === "") {
          return "";
        }
        return `
        <div class="store-locator-item__hours-item">
          <span class="store-locator-item__hours-day">${dayNames[index]}</span>
          <span class="store-locator-item__hours-time">${dayHours}</span>
        </div>
      `;
      })
      .filter((item) => item !== "") // Filtrer les jours vides
      .join("");
  }

  /**
   * Rendu du conteneur de carte
   */
  renderMapContainer() {
    // Vérifier si le conteneur existe déjà
    const existingContainer = document.getElementById("google-map");
    if (existingContainer) {
      return existingContainer.outerHTML;
    }
    return '<div id="google-map" class="store-locator-map" style="width: 100%; height: 400px; min-height: 400px; border-radius: 8px; overflow: hidden;"></div>';
  }

  /**
   * Rendu de la modal d'informations de magasin
   */
  renderStoreModal() {
    return `
      <div class="store-modal" id="store-modal" style="display: none;">
        <div class="store-modal__overlay" id="store-modal-overlay"></div>
        <div class="store-modal__content">
          <button class="store-modal__close" id="store-modal-close" aria-label="Fermer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="store-modal__body" id="store-modal-body">
            <!-- Le contenu sera rempli dynamiquement -->
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Rendu de l'état de chargement
   */
  renderLoading() {
    this.container.innerHTML = `
      <div class="store-locator-loading">
        <div class="store-locator-loading__spinner">
          <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
        <p>Chargement des magasins...</p>
      </div>
    `;
  }

  /**
   * Rendu de l'état d'erreur
   */
  renderError(message) {
    this.container.innerHTML = `
      <div class="store-locator-error">
        <h3>Erreur</h3>
        <p>${message}</p>
        <button onclick="location.reload()">Recharger la page</button>
      </div>
    `;
  }

  /**
   * Mise à jour de l'interface utilisateur
   */
  updateUI() {
    if (this.state.isLoading) {
      this.renderLoading();
      return;
    }

    // Mise à jour de la liste des magasins
    const listContainer = document.getElementById("store-list");
    if (listContainer) {
      listContainer.innerHTML = this.renderStoreList();
    }

    // Mise à jour de la carte
    if (this.state.showMap && !this.state.map) {
      this.initMap();
    }

    // Mise à jour des boutons de vue
    this.updateViewToggle();
  }

  /**
   * Mise à jour des boutons de basculement de vue et des conteneurs
   */
  updateViewToggle() {
    // Sur desktop, ne pas mettre à jour les boutons (ils sont cachés)
    if (this.isDesktop) {
      // Mise à jour des conteneurs de vue (toujours actifs sur desktop)
      const listContainer = this.container.querySelector(
        ".store-locator__list",
      );
      const mapContainer = this.container.querySelector(".store-locator__map");

      if (listContainer) {
        listContainer.classList.add("active");
      }

      if (mapContainer) {
        mapContainer.classList.add("active");
      }
      return;
    }

    // Mise à jour des boutons de basculement (mobile uniquement)
    const toggleButtons = this.container.querySelectorAll(
      ".store-locator__toggle-btn",
    );
    toggleButtons.forEach((btn) => {
      const view = btn.dataset.view;
      btn.classList.toggle(
        "active",
        (view === "list" && this.state.showList) ||
          (view === "map" && this.state.showMap),
      );
    });

    // Mise à jour des conteneurs de vue
    const listContainer = this.container.querySelector(".store-locator__list");
    const mapContainer = this.container.querySelector(".store-locator__map");

    if (listContainer) {
      listContainer.classList.toggle("active", this.state.showList);
    }

    if (mapContainer) {
      mapContainer.classList.toggle("active", this.state.showMap);
    }
  }

  /**
   * Initialisation de la carte Google Maps (mode standard)
   */
  async initMap() {
    if (!this.state.map) {
      await this.initGMap();
    }

    // Créer les marqueurs si la carte est prête
    if (this.state.map && this.state.filteredStores.length > 0) {
      this.createMarkers();
    }

    // Forcer le redimensionnement de la carte quand elle devient visible
    if (this.state.map) {
      setTimeout(() => {
        google.maps.event.trigger(this.state.map, "resize");
      }, 100);
    }
  }

  /**
   * Création des marqueurs sur la carte
   */
  createMarkers() {
    if (!this.state.map || !this.state.google) return;

    // Nettoyage des marqueurs existants
    this.clearMarkers();

    this.state.markers = [];
    this.state.filteredStores.forEach((store) => {
      if (
        store.content?.location?.latitude &&
        store.content?.location?.longitude
      ) {
        const markerIcon = this.getMarkerIcon(store);
        const marker = new google.maps.Marker({
          position: {
            lat: parseFloat(store.content.location.latitude),
            lng: parseFloat(store.content.location.longitude),
          },
          // ❌ SUPPRIMÉ: map: this.state.map, (les marqueurs ne doivent pas être ajoutés directement à la carte)
          title: store.content.name || store.name,
          icon: markerIcon,
          animation: google.maps.Animation.DROP,
          optimized: false, // Désactiver l'optimisation pour préserver la taille des markers
        });

        // Gestion du clic sur le marqueur
        marker.addListener("click", () => {
          this.openStoreModal(store);
        });

        // Stocker la référence du store dans le marqueur
        marker.store = store;
        this.state.markers.push(marker);
      }
    });

    // Configuration du clustering avec la nouvelle API
    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      const iconUrl = this.config.iconMarkerReseller;
      const img = new Image();
      const initClusterer = (w, h) => {
        this.state.markerClusterer = new window.markerClusterer.MarkerClusterer(
          {
            map: this.state.map,
            markers: this.state.markers,
            renderer: {
              render: ({ count, position }) => {
                return new google.maps.Marker({
                  position,
                  icon: {
                    url: iconUrl,
                    scaledSize: new google.maps.Size(w, h),
                    anchor: new google.maps.Point(w / 2, h),
                    labelOrigin: new google.maps.Point(w / 2, h - 18),
                  },
                  label: {
                    text: count.toString(),
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                  },
                  zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
              },
            },
            algorithmOptions: {
              maxZoom: this.config.maxZoom || 15,
            },
          },
        );
      };
      const baseSize = 40;
      img.onload = () => {
        const w = img.naturalWidth || baseSize;
        const h = img.naturalHeight || baseSize;
        const ratio = h / w;
        initClusterer(baseSize, baseSize * ratio);
      };
      img.onerror = () => {
        initClusterer((baseSize * 11) / 15, baseSize);
      };
      img.src = iconUrl;

      // Fonction pour forcer la taille correcte des markers
      const forceMarkerSizes = () => {
        this.state.markers.forEach((marker) => {
          if (marker.store) {
            const expectedIcon = this.getMarkerIcon(marker.store);
            const currentIcon = marker.getIcon();
            // Vérifier si la taille est correcte
            if (
              !currentIcon ||
              currentIcon.scaledSize?.width !==
                expectedIcon.scaledSize?.width ||
              currentIcon.scaledSize?.height !== expectedIcon.scaledSize?.height
            ) {
              marker.setIcon(expectedIcon);
            }
          }
        });
      };

      // Ajouter un listener sur le zoom pour forcer la taille des markers après le zoom
      // Le problème est que le MarkerClusterer peut modifier les markers après le zoom
      google.maps.event.addListener(this.state.map, "zoom_changed", () => {
        // Forcer la taille des markers après le zoom avec plusieurs tentatives
        setTimeout(() => forceMarkerSizes(), 100);
        setTimeout(() => forceMarkerSizes(), 300);
        setTimeout(() => forceMarkerSizes(), 500);
      });

      // Écouter l'événement 'idle' qui se déclenche après que tous les rendus soient terminés
      google.maps.event.addListener(this.state.map, "idle", () => {
        // Forcer la taille des markers après que la carte soit complètement rendue
        setTimeout(() => forceMarkerSizes(), 50);
        setTimeout(() => forceMarkerSizes(), 200);
      });

      // Vérification périodique pour s'assurer que les markers conservent leur taille
      // Cela corrige le problème si le MarkerClusterer modifie les markers après le rendu
      setInterval(() => {
        if (this.state.map && this.state.markers.length > 0) {
          forceMarkerSizes();
        }
      }, 2000); // Vérifier toutes les 2 secondes
    } else if (window.MarkerClusterer) {
      // Fallback pour l'ancienne API
      this.state.markerClusterer = new MarkerClusterer(
        this.state.map,
        this.state.markers,
        {
          imagePath: this.config.iconMarkerReseller, // Utiliser iconMarkerReseller pour les clusters
          maxZoom: this.config.maxZoom,
          gridSize: this.config.gridSize,
        },
      );
    } else {
      console.warn(
        "⚠️ MarkerClusterer non disponible, affichage des marqueurs individuels",
      );
      // Fallback: ajouter les marqueurs directement à la carte
      this.state.markers.forEach((marker) => {
        marker.setMap(this.state.map);
      });
    }
  }

  /**
   * Nettoyage des marqueurs
   */
  clearMarkers() {
    // Nettoyage via MarkerClusterer si disponible
    if (this.state.markerClusterer) {
      if (this.state.markerClusterer.clearMarkers) {
        this.state.markerClusterer.clearMarkers();
      } else if (this.state.markerClusterer.markers) {
        // Nouvelle API
        this.state.markerClusterer.markers.forEach((marker) =>
          marker.setMap(null),
        );
      }
    } else {
      // Fallback: nettoyage manuel
      this.state.markers.forEach((marker) => marker.setMap(null));
    }

    this.state.markers = [];
    this.state.markerClusterer = null;
  }

  /**
   * Obtention de l'icône de marqueur selon le type
   * - iconMarker : pour tous les points de vente
   * - iconMarkerReseller : pour les clusters (non utilisé dans cette fonction)
   * - iconMarkerActive : supprimé
   */
  getMarkerIcon(store) {
    // Tous les points de vente utilisent la même icône
    return {
      url: this.config.iconMarker,
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 40),
    };
  }

  /**
   * Sélection d'un magasin
   */
  selectStore(store) {
    this.state.currentStore = store;

    // Mise à jour de l'interface
    this.updateUI();

    // Recentrage de la carte si nécessaire
    if (
      this.state.map &&
      store.content?.location?.latitude &&
      store.content?.location?.longitude
    ) {
      this.state.map.setCenter({
        lat: parseFloat(store.content.location.latitude),
        lng: parseFloat(store.content.location.longitude),
      });
      this.state.map.setZoom(15);
    }

    // Mise à jour des marqueurs actifs
    this.updateActiveMarkers();

    // Scroll vers le magasin sélectionné dans la liste
    this.scrollToStore(store);
  }

  /**
   * Ouverture de la modal avec les informations du magasin
   */
  openStoreModal(store) {
    const modal = document.getElementById("store-modal");
    const modalBody = document.getElementById("store-modal-body");

    if (!modal || !modalBody) {
      console.error("Modal non trouvée dans le DOM");
      return;
    }

    // Remplir le contenu de la modal
    modalBody.innerHTML = this.renderStoreModalContent(store);

    // Afficher la modal
    modal.style.display = "block";
    document.body.style.overflow = "hidden"; // Empêcher le scroll de la page

    // Animation d'ouverture
    setTimeout(() => {
      modal.classList.add("store-modal--open");
    }, 10);
  }

  /**
   * Fermeture de la modal
   */
  closeStoreModal() {
    const modal = document.getElementById("store-modal");

    if (!modal) {
      console.error("Modal non trouvée dans le DOM");
      return;
    }

    // Animation de fermeture
    modal.classList.remove("store-modal--open");

    setTimeout(() => {
      modal.style.display = "none";
      document.body.style.overflow = ""; // Restaurer le scroll de la page
    }, 300);
  }

  /**
   * Rendu du contenu de la modal
   */
  renderStoreModalContent(store) {
    const distance = store.distance ? `${store.distance.toFixed(1)} km` : "";

    return `
      <div class="store-modal__header">
        <h2 class="store-modal__title">${store.content?.name || store.name}</h2>
        ${
          distance
            ? `<span class="store-modal__distance">${distance}</span>`
            : ""
        }
      </div>
      
      <div class="store-modal__info">
        <div class="store-modal__section">
          <h3 class="store-modal__section-title">Adresse</h3>
          <div class="store-modal__address">
            <p>${store.content?.location?.address || ""}</p>
            <p>${store.content?.location?.postal_code || ""} ${
              store.content?.location?.town || ""
            }</p>
          </div>
        </div>
        
        ${
          (store.content?.contact?.phone &&
            store.content.contact.phone.trim() !== "" &&
            store.content.contact.phone !== null) ||
          (store.content?.contact?.email &&
            store.content.contact.email.trim() !== "" &&
            store.content.contact.email !== null)
            ? `
        <div class="store-modal__section">
          <h3 class="store-modal__section-title">Contact</h3>
          <div class="store-modal__contact">
            ${
              store.content?.contact?.phone &&
              store.content.contact.phone.trim() !== "" &&
              store.content.contact.phone !== null
                ? `
              <a href="tel:${store.content.contact.phone}" class="store-modal__phone">
                <svg class="icon icon-phone icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H8.15287C8.64171 3 9.05816 3.35341 9.13927 3.8356L9.87858 7.27147C9.95475 7.72651 9.73606 8.17726 9.33967 8.40699L7.79131 9.40552C8.9141 11.8113 10.6887 13.5859 13.0945 14.7087L14.093 13.1603C14.3227 12.7639 14.7735 12.5452 15.2285 12.6214L18.6644 13.3607C19.1466 13.4418 19.5 13.8583 19.5 14.3471V17.5C19.5 18.0304 19.2893 18.5391 18.9142 18.9142C18.5391 19.2893 18.0304 19.5 17.5 19.5H16.5C8.54416 19.5 2.25 13.2058 2.25 5.25V4.5C2.25 3.96957 2.46071 3.46086 2.83579 3.08579C3.21086 2.71071 3.71957 2.5 4.25 2.5H7.5C8.03043 2.5 8.53914 2.71071 8.91421 3.08579C9.28929 3.46086 9.5 3.96957 9.5 4.5V5.25"></path>
                </svg>
                ${store.content.contact.phone}
              </a>
            `
                : ""
            }
            ${
              store.content?.contact?.email &&
              store.content.contact.email.trim() !== "" &&
              store.content.contact.email !== null
                ? `
              <a href="mailto:${store.content.contact.email}" class="store-modal__email">
                <svg class="icon icon-email icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"></path>
                </svg>
                ${store.content.contact.email}
              </a>
            `
                : ""
            }
          </div>
        </div>
        `
            : ""
        }
        
        ${
          this.hasOpeningHours(store.content?.opening_hours)
            ? `
        <div class="store-modal__section">
          <h3 class="store-modal__section-title">Horaires d'ouverture</h3>
          <div class="store-modal__hours">
            ${this.renderOpeningHours(store.content.opening_hours)}
          </div>
        </div>
        `
            : ""
        }
        
        ${
          store.content?.type_of_store
            ? `
        <div class="store-modal__section">
          <h3 class="store-modal__section-title">Type de magasin</h3>
          <div class="store-modal__type">
            <span class="store-modal__type-badge">${store.content.type_of_store}</span>
          </div>
        </div>
        `
            : ""
        }
      </div>
      
      <div class="store-modal__actions">
        ${
          store.content?.location?.latitude &&
          store.content?.location?.longitude
            ? `
        <a 
          href="https://www.google.com/maps/dir/?api=1&destination=${
            store.content.location.latitude
          },${store.content.location.longitude}" 
          target="_blank" 
          class="store-modal__directions"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          ${this.state.translations?.content?.directions || "Itinéraire"}
        </a>
        `
            : ""
        }
      </div>
    `;
  }

  /**
   * Mise à jour des marqueurs actifs
   */
  updateActiveMarkers() {
    this.state.markers.forEach((marker) => {
      const isActive =
        this.state.currentStore &&
        marker.store &&
        marker.store.slug === this.state.currentStore.slug;

      const newIcon = isActive
        ? this.getMarkerIcon(this.state.currentStore)
        : this.getMarkerIcon(marker.store);
      marker.setIcon(newIcon);
    });
  }

  /**
   * Scroll vers un magasin dans la liste
   */
  scrollToStore(store) {
    const storeElement = document.querySelector(
      `[data-store-slug="${store.slug}"]`,
    );
    if (storeElement) {
      storeElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * Recherche et filtrage des magasins
   */
  async searchStores(address, type, radius) {
    try {
      let filteredStores = [...this.state.stores];

      // Filtrage par type
      if (type && type !== "all") {
        filteredStores = filteredStores.filter(
          (store) =>
            store.content?.type_of_store?.toLowerCase() === type.toLowerCase(),
        );
      }

      // Recherche par adresse et calcul des distances
      if (address?.trim()) {
        const coordinates = await this.geocodeAddress(address);
        if (coordinates) {
          this.state.currentCoordinates = coordinates;

          // Calcul des distances
          filteredStores = this.calculateDistances(filteredStores, coordinates);

          // Filtrage par rayon
          if (radius && radius > 0) {
            filteredStores = filteredStores.filter(
              (store) => store.distance <= radius,
            );
          }

          // Tri par distance
          filteredStores.sort((a, b) => (a.distance || 0) - (b.distance || 0));

          // Recentrage de la carte
          if (this.state.map) {
            this.state.map.setCenter(coordinates);
            this.state.map.setZoom(10);
          }
        }
      }

      this.state.filteredStores = filteredStores;
      this.updateUI();
      this.createMarkers();
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
    }
  }

  /**
   * Géocodage d'une adresse
   */
  async geocodeAddress(address) {
    if (!this.state.geocoder) return null;

    return new Promise((resolve, reject) => {
      this.state.geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            geometry: results[0].geometry,
          });
        } else {
          reject(new Error(`Erreur de géocodage: ${status}`));
        }
      });
    });
  }

  /**
   * Calcul des distances entre les magasins et un point de référence
   */
  calculateDistances(stores, referencePoint) {
    return stores.map((store) => {
      if (
        store.content?.location?.latitude &&
        store.content?.location?.longitude
      ) {
        let distance;

        // Utiliser Google Maps geometry si disponible
        if (window.google?.maps?.geometry?.spherical) {
          distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(referencePoint.lat, referencePoint.lng),
            new google.maps.LatLng(
              parseFloat(store.content.location.latitude),
              parseFloat(store.content.location.longitude),
            ),
          );
        } else {
          // Calcul de distance simplifié (formule de Haversine)
          distance = this.calculateHaversineDistance(
            referencePoint.lat,
            referencePoint.lng,
            parseFloat(store.content.location.latitude),
            parseFloat(store.content.location.longitude),
          );
        }

        return {
          ...store,
          distance: parseFloat((distance / 1000).toFixed(1)), // Conversion en km
        };
      }
      return store;
    });
  }

  /**
   * Calcul de distance simplifié (formule de Haversine)
   */
  calculateHaversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Rayon de la Terre en mètres
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance en mètres
  }

  /**
   * Conversion de degrés en radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Géolocalisation de l'utilisateur
   */
  async getCurrentPosition() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Géolocalisation non supportée"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        },
      );
    });
  }

  /**
   * Basculement entre vue liste et carte
   */
  switchView(view) {
    // Sur desktop, ne pas permettre le basculement
    if (this.isDesktop) {
      return;
    }

    this.state.showList = view === "list";
    this.state.showMap = view === "map";

    // Mise à jour des classes active sur les conteneurs
    this.updateViewToggle();

    this.updateUI();

    // Initialisation de la carte si nécessaire
    if (this.state.showMap && !this.state.map) {
      setTimeout(() => this.initMap(), 100);
    } else if (this.state.showMap && this.state.map) {
      // Si la carte existe déjà, forcer le redimensionnement quand elle devient visible
      setTimeout(() => {
        google.maps.event.trigger(this.state.map, "resize");
      }, 300);
    }
  }

  /**
   * Gestion du redimensionnement de la fenêtre
   */
  handleResize() {
    let resizeTimeout;
    const handleResizeEvent = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth > 1000;

        // Si on passe de mobile à desktop ou vice versa
        if (wasDesktop !== this.isDesktop) {
          if (this.isDesktop) {
            // Passage en mode desktop : afficher les deux vues
            this.state.showList = true;
            this.state.showMap = true;

            // Initialiser la carte si elle n'existe pas encore
            if (!this.state.map) {
              this.initGMap().then(() => {
                if (this.state.map && this.state.filteredStores.length > 0) {
                  this.createMarkers();
                }
              });
            } else {
              // Redimensionner la carte existante
              setTimeout(() => {
                if (this.state.map) {
                  google.maps.event.trigger(this.state.map, "resize");
                }
              }, 100);
            }
          } else {
            // Passage en mode mobile : garder seulement la liste par défaut
            this.state.showList = true;
            this.state.showMap = false;
          }

          this.updateUI();
        }
      }, 250);
    };

    window.addEventListener("resize", handleResizeEvent);

    // Stocker le gestionnaire pour pouvoir le nettoyer si nécessaire
    this.resizeHandler = handleResizeEvent;
  }

  /**
   * Réinitialisation de la recherche
   */
  resetSearch() {
    this.state.searchAddress = "";
    this.state.selectedType = "all";
    this.state.selectedRadius = 0;
    this.state.currentCoordinates = null;
    this.state.currentStore = null;
    this.state.filteredStores = [...this.state.stores];

    // Mise à jour des champs de formulaire
    const addressInput = document.getElementById("search-address");
    const typeSelect = document.getElementById("store-type");
    const radiusSelect = document.getElementById("search-radius");

    if (addressInput) {
      addressInput.value = "";
      // Déclencher l'événement pour mettre à jour le label flottant
      addressInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
    if (typeSelect) {
      typeSelect.value = "all";
      // Déclencher l'événement pour mettre à jour le label flottant
      typeSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (radiusSelect) {
      radiusSelect.value = "0";
      // Déclencher l'événement pour mettre à jour le label flottant
      radiusSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.updateUI();
    this.createMarkers();

    // Recentrage de la carte
    if (this.state.map) {
      this.state.map.setCenter(this.config.defaultCenter);
      this.state.map.setZoom(this.config.defaultZoom);
    }
  }

  /**
   * Liaison des événements
   */
  bindEvents() {
    // Vérifier si GraphQL a déjà chargé les données (race condition fix)
    // Si les données GraphQL sont déjà disponibles, les utiliser directement
    const checkGraphQLData = () => {
      if (
        window.shopifyStoresConfig?.useGraphQL === true &&
        window.shopifyStoresData &&
        Array.isArray(window.shopifyStoresData) &&
        window.shopifyStoresData.length > 250 &&
        this.state.stores.length < window.shopifyStoresData.length
      ) {
        this.state.stores = window.shopifyStoresData;
        this.state.filteredStores = [...this.state.stores];
        this.extractTypes();
        this.updateUI();
        if (this.state.map && this.state.filteredStores.length > 0) {
          this.createMarkers();
        }
      }
    };

    // Vérifier immédiatement
    checkGraphQLData();

    // Écouter l'événement shopifyStoresLoaded pour recharger les données GraphQL
    window.addEventListener("shopifyStoresLoaded", (event) => {
      if (
        event.detail &&
        event.detail.stores &&
        Array.isArray(event.detail.stores)
      ) {
        this.state.stores = event.detail.stores;
        this.state.filteredStores = [...this.state.stores];
        this.extractTypes();
        this.updateUI();
        if (this.state.map && this.state.filteredStores.length > 0) {
          this.createMarkers();
        }
      }
    });

    // Formulaire de recherche
    const searchForm = document.getElementById("search-form");
    if (searchForm) {
      searchForm.addEventListener("submit", async (e) => {
        e.preventDefault();
        const addressInput = document.getElementById("search-address");
        const typeSelect = document.getElementById("store-type");
        const radiusSelect = document.getElementById("search-radius");
        const submitButton = searchForm.querySelector('button[type="submit"]');

        const address = addressInput ? addressInput.value : "";
        const type = typeSelect ? typeSelect.value : "all";
        const radius = radiusSelect ? parseInt(radiusSelect.value) : 0;

        this.state.searchAddress = address;
        this.state.selectedType = type;
        this.state.selectedRadius = radius;

        // Mettre à jour les labels flottants des selects
        if (typeSelect && typeSelect.value) {
          typeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
        if (radiusSelect && radiusSelect.value) {
          radiusSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }

        try {
          await this.searchStores(address, type, radius);
        } finally {
          // Retirer l'attribut aria-busy après la recherche (succès ou erreur)
          if (submitButton) {
            submitButton.removeAttribute("aria-busy");
          }
        }
      });
    }

    // Gestionnaires pour mettre à jour les labels flottants des selects
    const typeSelect = document.getElementById("store-type");
    const radiusSelect = document.getElementById("search-radius");

    if (typeSelect) {
      typeSelect.addEventListener("change", () => {
        // Le CSS du thème gère automatiquement l'animation du label
      });
    }

    if (radiusSelect) {
      radiusSelect.addEventListener("change", () => {
        // Le CSS du thème gère automatiquement l'animation du label
      });
    }

    // Bouton de géolocalisation
    const geolocationBtn = document.getElementById("geolocation-btn");
    if (geolocationBtn) {
      geolocationBtn.addEventListener("click", async () => {
        try {
          const position = await this.getCurrentPosition();
          const addressInput = document.getElementById("search-address");
          if (addressInput) {
            addressInput.value = "Ma position actuelle";
            // Déclencher l'événement pour mettre à jour le label flottant
            addressInput.dispatchEvent(new Event("input", { bubbles: true }));
          }

          this.state.currentCoordinates = position;
          this.searchStores(
            "",
            this.state.selectedType,
            this.state.selectedRadius,
          );

          if (this.state.map) {
            this.state.map.setCenter(position);
            this.state.map.setZoom(12);
          }
        } catch (error) {
          console.error("Erreur de géolocalisation:", error);
          alert(
            "Impossible d'obtenir votre position. Vérifiez les permissions de géolocalisation.",
          );
        }
      });
    }

    // Bouton de réinitialisation
    const resetBtn = document.getElementById("reset-search");
    if (resetBtn) {
      resetBtn.addEventListener("click", () => this.resetSearch());
    }

    // Boutons de basculement de vue
    const toggleButtons = this.container.querySelectorAll(
      ".store-locator__toggle-btn",
    );
    toggleButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const view = btn.dataset.view;
        this.switchView(view);
      });
    });

    // Sélection de magasin dans la liste
    this.container.addEventListener("click", (e) => {
      // Utiliser closest() pour gérer les clics sur les enfants du bouton (btn-text, btn-fill, etc.)
      const selectButton = e.target.closest(".store-locator-item__select");
      if (selectButton) {
        const storeSlug = selectButton.dataset.storeSlug;
        const store = this.state.filteredStores.find(
          (s) => s.slug === storeSlug,
        );
        if (store) {
          // Si on est en mode liste uniquement (mobile), basculer vers la carte
          // Sur desktop, les deux sont déjà visibles donc pas besoin de basculer
          if (!this.isDesktop && this.state.showList && !this.state.showMap) {
            this.switchView("map");
          }
          this.selectStore(store);
        }
      }
    });

    // Gestion des événements de la modal
    this.bindModalEvents();
  }

  /**
   * Liaison des événements de la modal
   */
  bindModalEvents() {
    // Fermeture de la modal par clic sur l'overlay ou à l'extérieur
    document.addEventListener("click", (e) => {
      const modal = document.getElementById("store-modal");
      if (modal && modal.style.display === "block") {
        // Fermer si on clique sur l'overlay ou sur la modal elle-même (mais pas sur le contenu)
        if (
          e.target.id === "store-modal-overlay" ||
          (e.target.id === "store-modal" &&
            !e.target.closest(".store-modal__content"))
        ) {
          this.closeStoreModal();
        }
      }
    });

    // Fermeture de la modal par clic sur le bouton de fermeture
    document.addEventListener("click", (e) => {
      if (
        e.target.id === "store-modal-close" ||
        e.target.closest("#store-modal-close")
      ) {
        this.closeStoreModal();
      }
    });

    // Fermeture de la modal par la touche Échap
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        const modal = document.getElementById("store-modal");
        if (modal && modal.style.display === "block") {
          this.closeStoreModal();
        }
      }
    });
  }

  /**
   * Gestion centralisée des erreurs
   */
  handleError(error, context = "") {
    console.error(`StoreLocator Error [${context}]:`, error);

    // Utiliser la gestion d'erreurs de GMap si disponible
    if (this.gmapInstance && window.mapError) {
      window.mapError(error.message, this.container);
    } else {
      // Fallback vers notre gestion d'erreurs
      this.renderError(error.message);
    }

    // Tracking des erreurs
    if (window.analytics) {
      analytics.track("store_locator_error", {
        error: error.message,
        context: context,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Gestion du cache
   */
  setCachedData(key, data, ttl = 300000) {
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl,
    };
    this.cache.set(key, cacheData);
  }

  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  /**
   * Nettoyage des ressources
   */
  destroy() {
    // Nettoyage des marqueurs
    this.clearMarkers();

    // Nettoyage des événements
    this.eventListeners.forEach((listener, element) => {
      element.removeEventListener(listener.event, listener.handler);
    });
    this.eventListeners.clear();

    // Nettoyage du gestionnaire de redimensionnement
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }

    // Nettoyage du cache
    this.cache.clear();

    // Nettoyage du DOM
    if (this.container) {
      this.container.innerHTML = "";
    }
  }
}

// Export pour utilisation dans les modules
if (typeof module !== "undefined" && module.exports) {
  module.exports = StoreLocator;
}

// Initialisation automatique si configuré
if (typeof window !== "undefined") {
  window.StoreLocator = StoreLocator;

  // Auto-initialisation si les données sont disponibles
  document.addEventListener("DOMContentLoaded", () => {
    const container = document.getElementById("store-locator");
    if (container && window.storeLocatorConfig) {
      window.storeLocatorInstance = new StoreLocator(window.storeLocatorConfig);
      // Lancer l'initialisation
      window.storeLocatorInstance.init();
    }
  });
}
